import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSubscription } from "../lib/subscription";
import { toast } from "sonner";
import { Check, Loader2, Star, Ticket, ShieldCheck, AlertTriangle } from "lucide-react";

export default function SubscriptionPage() {
  const { isAdmin } = useAuth();
  const { sub, refresh } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, type, value}
  const [applying, setApplying] = useState(false);
  const [paying, setPaying] = useState(null); // plan_id
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    api.get("/subscription/plans").then((r) => setPlans(r.data?.plans || [])).catch(() => {});
  }, []);

  // Returning from Stripe — poll payment status
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    setPolling(true);
    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 8) {
        setPolling(false);
        toast.error("تعذر التحقق من حالة الدفع. إذا خُصم المبلغ سيُفعَّل اشتراكك تلقائيًا.");
        setSearchParams({}, { replace: true });
        return;
      }
      attempts += 1;
      try {
        const r = await api.get(`/subscription/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setPolling(false);
          toast.success("تم الدفع بنجاح! تم تفعيل اشتراكك 🎉");
          setSearchParams({}, { replace: true });
          refresh();
          return;
        }
        if (r.data.status === "expired") {
          setPolling(false);
          toast.error("انتهت صلاحية جلسة الدفع. حاول مرة أخرى.");
          setSearchParams({}, { replace: true });
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setApplying(true);
    try {
      const r = await api.post("/subscription/validate-coupon", { code: couponInput.trim() });
      setCoupon(r.data);
      toast.success(`تم تطبيق الكوبون: خصم ${r.data.type === "percent" ? `${r.data.value}%` : `${r.data.value} ريال`}`);
    } catch (e) {
      setCoupon(null);
      toast.error(e?.response?.data?.detail || "الكوبون غير صالح");
    } finally {
      setApplying(false);
    }
  };

  const discounted = useMemo(() => {
    if (!coupon) return null;
    const map = {};
    plans.forEach((p) => {
      const d = coupon.type === "percent" ? (p.price * coupon.value) / 100 : coupon.value;
      map[p.id] = Math.max(p.price - d, 0);
    });
    return map;
  }, [coupon, plans]);

  const choosePlan = async (planId) => {
    setPaying(planId);
    try {
      const r = await api.post("/subscription/checkout", {
        plan_id: planId,
        coupon_code: coupon?.code || null,
        origin_url: window.location.origin,
      });
      if (r.data.free) {
        toast.success("تم تفعيل اشتراكك مجانًا بالكوبون 🎉");
        refresh();
        setPaying(null);
        return;
      }
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر بدء عملية الدفع");
      setPaying(null);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("ar-SA-u-nu-latn", { day: "numeric", month: "long", year: "numeric" }) : "—";

  return (
    <div className="space-y-6 fade-up" data-testid="subscription-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">الاشتراك</div>
        <h1 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">الاشتراك</h1>
        <p className="text-[#5C605A] mt-2">اختر الخطة المناسبة لاحتياجاتك</p>
      </div>

      {polling && (
        <div className="bg-[#FDF6E9] border border-[#E8B96E]/50 rounded-md p-4 flex items-center gap-3 text-[#7A5B16]" data-testid="payment-polling-banner">
          <Loader2 className="w-5 h-5 animate-spin shrink-0" />
          <span className="font-readex">جاري التحقق من حالة الدفع…</span>
        </div>
      )}

      {/* Current subscription */}
      {sub && !isAdmin && (
        <div className="bg-white border border-[#1A1C19]/10 rounded-lg p-5 sm:p-6 shadow-3d-sm" data-testid="current-subscription-card">
          <h2 className="font-readex font-semibold text-lg text-[#1A1C19] mb-5">اشتراكك الحالي</h2>
          {sub.plan_id ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                <InfoItem label="الخطة" value={sub.plan_name || "—"} testid="sub-plan-name" />
                <InfoItem label="تاريخ البداية" value={fmtDate(sub.start_date)} />
                <InfoItem label="تاريخ الانتهاء" value={fmtDate(sub.end_date)} />
                <InfoItem
                  label="الحالة"
                  value={sub.active ? "نشط" : "منتهي"}
                  valueClass={sub.active ? "text-[#1F3A33]" : "text-[#C84C31]"}
                  testid="sub-status"
                />
                <InfoItem label="الأيام المتبقية" value={String(sub.days_remaining ?? 0)} testid="sub-days-remaining" />
              </div>
              {sub.is_trial && sub.active && (
                <div className="mt-4 bg-[#EAF2ED] border border-[#6A8A76]/30 rounded-md px-4 py-3 text-sm text-[#1F3A33] font-readex flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 shrink-0" />
                  أنت في الفترة التجريبية المجانية — اشترك الآن لضمان استمرار الخدمة بدون انقطاع.
                </div>
              )}
              {!sub.active && (
                <div className="mt-4 bg-[#FBE9E3] border border-[#C84C31]/30 rounded-md px-4 py-3 text-sm text-[#7A2516] font-readex flex items-center gap-2" data-testid="sub-expired-banner">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  انتهى اشتراكك — التطبيق مقفل حتى تجديد الاشتراك من الخطط أدناه.
                </div>
              )}
            </>
          ) : (
            <p className="text-[#5C605A] font-readex">لا يوجد اشتراك بعد — اختر خطة من الأسفل للبدء.</p>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="bg-[#EAF2ED] border border-[#6A8A76]/30 rounded-md px-4 py-3 text-sm text-[#1F3A33] font-readex flex items-center gap-2" data-testid="admin-exempt-banner">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          حساب مسؤول النظام معفي من الاشتراك.
        </div>
      )}

      {/* Plans */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plans-grid">
        {plans.map((p) => {
          const finalPrice = discounted ? discounted[p.id] : null;
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative bg-white rounded-lg p-5 flex flex-col border transition-all ${
                p.popular
                  ? "border-[#1F3A33] shadow-[0_8px_24px_rgba(31,58,51,0.15)]"
                  : "border-[#1A1C19]/10 shadow-3d-sm"
              }`}
            >
              {p.popular && (
                <div className="absolute -top-3 start-1/2 translate-x-1/2 bg-[#1F3A33] text-white text-xs font-readex px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                  <Star className="w-3 h-3 fill-current" />
                  الأكثر شيوعًا
                </div>
              )}
              <div className="font-readex font-semibold text-lg text-[#1A1C19]">{p.name_ar}</div>
              <div className="text-xs text-[#8A8D86] mt-0.5 mb-4">{p.duration_ar}</div>
              <div className="mb-5">
                {finalPrice !== null && finalPrice !== p.price ? (
                  <>
                    <span className="text-sm text-[#8A8D86] line-through font-mono-num me-2">{p.price.toFixed(2)}</span>
                    <span className="font-mono-num font-semibold text-3xl text-[#1F3A33]">{finalPrice.toFixed(2)}</span>
                    <span className="text-sm text-[#5C605A] ms-1">SAR</span>
                  </>
                ) : (
                  <>
                    <span className="font-mono-num font-semibold text-3xl text-[#1A1C19]">{p.price.toFixed(2)}</span>
                    <span className="text-sm text-[#5C605A] ms-1">SAR</span>
                  </>
                )}
              </div>
              <ul className="space-y-2.5 flex-1 mb-5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-[#1A1C19]">
                    <Check className="w-4 h-4 text-[#1F3A33] shrink-0" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choosePlan(p.id)}
                disabled={!!paying || isAdmin}
                data-testid={`choose-plan-${p.id}-btn`}
                className={`w-full rounded-md px-4 py-3 font-readex font-medium transition-all active:translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 ${
                  p.popular
                    ? "bg-[#1F3A33] text-white hover:bg-[#172A25]"
                    : "bg-white border border-[#1A1C19]/15 text-[#1A1C19] hover:border-[#1F3A33] hover:bg-[#F0EFEB]"
                }`}
              >
                {paying === p.id && <Loader2 className="w-4 h-4 animate-spin" />}
                اختيار الخطة
              </button>
            </div>
          );
        })}
      </div>

      {/* Coupon */}
      <div className="bg-white border border-[#1A1C19]/10 rounded-lg p-5 sm:p-6 shadow-3d-sm" data-testid="coupon-card">
        <h2 className="font-readex font-semibold text-lg text-[#1A1C19] mb-4 flex items-center gap-2">
          <Ticket className="w-5 h-5 text-[#6A8A76]" strokeWidth={1.5} />
          كوبون الخصم
        </h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value)}
            placeholder="أدخل الكوبون"
            data-testid="coupon-input"
            className="flex-1 bg-white border border-[#1A1C19]/15 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#1F3A33] focus:ring-2 focus:ring-[#1F3A33]/10"
            dir="ltr"
            style={{ textAlign: "end" }}
          />
          <button
            onClick={applyCoupon}
            disabled={applying || !couponInput.trim()}
            data-testid="apply-coupon-btn"
            className="bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 rounded-md px-6 py-3 font-readex font-medium inline-flex items-center justify-center gap-2"
          >
            {applying && <Loader2 className="w-4 h-4 animate-spin" />}
            تطبيق
          </button>
        </div>
        {coupon && (
          <div className="mt-3 text-sm text-[#1F3A33] font-readex flex items-center gap-2" data-testid="coupon-applied-note">
            <Check className="w-4 h-4" />
            كوبون <span className="font-mono-num" dir="ltr">{coupon.code}</span> مفعّل — خصم {coupon.type === "percent" ? `${coupon.value}%` : `${coupon.value} ريال`} يظهر على الأسعار أعلاه.
          </div>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value, valueClass = "text-[#1A1C19]", testid }) {
  return (
    <div>
      <div className="text-xs text-[#8A8D86] font-readex mb-1">{label}</div>
      <div className={`font-readex font-medium ${valueClass}`} data-testid={testid}>{value}</div>
    </div>
  );
}
